<template>
  <div>
    <ValidationObserver
      ref="fileObserver" >
      <ValidationProvider
        v-slot="{ errors }"
        :name="schema.title"
        :rules="validators">

        <b-button
          :disabled="(filesUploading && !filesFinishedUploading) || !!fileUploadCounterForAssistant || !!fileDeleteCounterForAssistant"
          class="file-button flex flex-align-center icon"
          :style="{ 'background-image': (filesUploading && !filesFinishedUploading) || !!fileUploadCounterForAssistant || !!fileDeleteCounterForAssistant ? 'unset' : schema.addButtonIcon ? `url(${schema.addButtonIcon})` : '' }"
          @click="addFiles">
          <span
            v-if="(filesUploading && !filesFinishedUploading) || !!fileUploadCounterForAssistant || !!fileDeleteCounterForAssistant"
            style="margin-left: -20px;"
            class="flex flex-align-center" >
            <CircleSpinner
              :diameter="14"
              :stroke-width="2"
              :show-tick="processingFinished"
              infinite
              foreground="#141c20"
              background="#CFD8DC" />
            <p class="action-text ml-2">Processing files...</p>
          </span>
          <span
            v-else-if="!fileCount"
            class="subtle" >{{ schema.addButtonText }}</span>
          <template v-else>
            <span class="ellipsis greedy text-left" >{{ fileCount | bigDigits }} file{{ fileCount === 1 ? '' : 's' }}</span>
            <SBIcon
              v-if="flags && flags.fail"
              class="ml-2"
              style="margin-right: -4px;"
              :icon="SBIconList.TRIANGLE_EXCLAMATION"
              :color="SBColors.redPrimary"
              :size="12" />
          </template>
        </b-button>
        <!-- SUBTITLE -->
        <div
          v-if="(filesUploading && !filesFinishedUploading) || !!fileUploadCounterForAssistant || !!fileDeleteCounterForAssistant"
          class="subtitle flex flex-align-center show-hand"
          @click="cancelUpload" >
          <SBIcon
            class="mr-1"
            :icon="SBIconList.CLOSE_THIN"
            :color="SBColors.redPrimary"
            :size="10" />
          <span class="subtle error">Cancel</span>
        </div>
        <div
          v-else-if="schema.subtitle && !errors[0]"
          class="subtitle flex flex-align-center">
          <svg
            v-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string' && schema.subtitlePrefixIcon.endsWith('.svg')"
            class="mr-1 flex-start-image"
            :data-src="schema.subtitlePrefixIcon"
            fill="currentColor"
            width="10"
            height="10" />
          <img
            v-else-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string'"
            :src="schema.subtitlePrefixIcon"
            style="width: 10px; height: 10px;"
            class="mr-1 flex-start-image"
          >
          <SBIcon
            v-else-if="!schema.noSubtitlePrefixIcon"
            class="mr-1"
            :icon="SBIconList.INFO"
            :color="SBColors.greyLight"
            :size="10" />
          <span v-html="schema.subtitle" />
        </div>
      </ValidationProvider>
    </ValidationObserver>

    <FileInputModal
      ref="fileInputModalRef"
      v-model="files"
      :allowed-file-types="schema.allowedFileTypes.split(',').map(ft => ft.trim())"
      :max-files="schema.maxFiles"
      :count="fileCount"
      :remote-files="{
        endpoint: schema.endpoint,
        searchTag: schema.searchTag,
      }"
      :modal-title="schema.modalTitle"
      :modal-subtitle="schema.modalSubtitle"
      @closed="modalClosed" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import CircleSpinner from '@/components/animatedIcons/CircleSpinner';
import api from '@/helpers/api';
import { ModalSize, ModalVariant } from '@/helpers/enums/modal.enum';
import FileInputModal from './FileInput.modal';
import StatusTypes from '../../../../helpers/enums/statusTypes.enum';

export default {
  name: 'FileInputWrapper',
  components: {
    FileInputModal,
    CircleSpinner,
    SBIcon,
  },
  props: {
    validators: {
      type: String,
      default: '',
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      files: {
        addFiles: [],
        removeFiles: [],
      },
      filesBackup: [],
      dirty: false,
      fileUploadStats: {
        total: 0,
        uploaded: 0,
        failed: 0,
      },
      counterOverride: null,
      newCountChange: 0,
      flags: null,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      backgroundActions: 'automation/backgroundActions',
      fileChangesTracker: 'automation/fileChangesTracker',
      fileUploadCounter: 'automation/fileUploadCounter',
      fileDeleteCounter: 'automation/fileDeleteCounter',
      tempFileCounter: 'automation/tempFileCounter',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    assistantId() {
      return this.$route.params.assistantId;
    },
    fileCount() {
      const baseCount = this.counterOverride !== null ? this.counterOverride : this.schema.count;
      // const tempFileCounter = this.tempFileCounter[this.assistantId] || { uploaded: 0, deleted: 0 };
      return baseCount + this.newCountChange; // + tempFileCounter.uploaded - tempFileCounter.deleted; // + this.fileChangesTracker.filesAdded - this.fileChangesTracker.filesRemoved
    },
    newCount() {
      return this.schema.count; // + this.fileChangesTracker.filesAdded - this.fileChangesTracker.filesRemoved;
    },
    fileUploadProgress() {
      return this.fileUploadStats.total ? (this.fileUploadStats.uploaded) / this.fileUploadStats.total * 100 : 0;
    },
    filesUploading() {
      return (this.backgroundActions.filter(ba => ba.action === 'uploadAiFiles').length > 0 && !!this.fileUploadStats.total);
    },
    filesFinishedUploading() {
      return this.backgroundActions.filter(ba => ba.action === 'uploadAiFiles').every(ba => ba.status === StatusTypes.COMPLETED);
    },
    fileUploadCounterForAssistant() {
      return this.fileUploadCounter[this.assistantId];
    },
    fileDeleteCounterForAssistant() {
      return this.fileDeleteCounter[this.assistantId];
    },
    processingFinished() {
      return (!this.fileUploadCounterForAssistant || this.fileUploadCounterForAssistant.percentage === 100) && (!this.fileDeleteCounterForAssistant || this.fileDeleteCounterForAssistant.percentage === 100);
    },
  },
  watch: {
    backgroundActions() {
      this.calculateFileUploadStats();
    },
    files: {
      handler() {
        if (this.dirty) {
          localStorage.setItem('aifiles:nCC', this.files.addFiles.length - this.files.removeFiles.length);
          this.newCountChange = Number.parseInt(localStorage.getItem('aifiles:nCC'), 10);
        }
        this.dirty = true;
        this.emitData();
      },
      immediate: true,
    },
    filesFinishedUploading() {
      if (this.filesFinishedUploading) {
        this.files.addFiles = [];
        this.updateFileChangeCounter({ filesAdded: this.fileUploadStats.uploaded });
      }
    },
  },
  mounted() {
    if (!localStorage.getItem('aifiles:nCC')) {
      localStorage.setItem('aifiles:nCC', 0);
      this.newCountChange = 0;
    } else {
      this.newCountChange = Number.parseInt(localStorage.getItem('aifiles:nCC'), 10);
    }
    if (!localStorage.getItem('aifiles:cO') || localStorage.getItem('aifiles:cO') === 'null') {
      localStorage.setItem('aifiles:cO', null);
      this.counterOverride = null;
    } else {
      this.counterOverride = Number.parseInt(localStorage.getItem('aifiles:cO'), 10);
    }
    if (this.schema.value) {
      // Set files on edit
    }
    this.flags = this.schema.flags;
    if (this.schema.stepID) {
      this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
        if (!this.$refs.fileObserver) return;
        this.$refs.fileObserver.validate();
      });
    }
    // Set new counters
    window.eventBus.$on('ai-files-update', (data) => {
      if (data.ai_assistant_uid === this.assistantId) {
        localStorage.setItem('aifiles:nCC', 0);
        this.newCountChange = 0;
        this.counterOverride = data.file_count;
        localStorage.setItem('aifiles:cO', this.counterOverride);
        this.flags = { ...this.flags, ...data.flags.files };
      }
    });
    // SET FLAGS
    try {
      if (this.schema.flags.delete) {
        this.updateFileDeleteCounter({ ai_assistant_uid: this.assistantId });
      }
      if (this.schema.flags.upload) {
        this.updateFileUploadCounter({ ai_assistant_uid: this.assistantId }); // , status: StatusTypes.RUNNING 
      }
    } catch (error) {}

    setTimeout(() => {
      this.emitData(true);
      if (this.schema.value) {
        if (!this.$refs.fileObserver) return;
        this.$refs.fileObserver.validate();
      }
    }, 0);
    this.$eventBus.$on('resetPostActions', () => {
      this.files.addFiles = [];
      this.updateFileChangeCounter({ filesAdded: this.fileUploadStats.uploaded });
      // this.files.removeFiles = []; // no need for this since it is reset on files-remove event, and if we remove it here then the counter changes too early
      this.$nextTick(() => {
        this.emitData();
        // this.$eventBus.$emit('clearAddFiles'); // no need for this. It will also trigger the refresh of the form which we do not want.
      });
    });
    window.eventBus.$on('files-removed', (payload) => {
      if (payload.assistantId === this.assistantId) {
        this.updateFileChangeCounter({ filesRemoved: payload.results.removed.length });
        this.$nextTick(() => {
          this.files.removeFiles = [];
          this.emitData();
        });
      }
    });
  },
  destroyed() {
    this.$eventBus.$off(`validate-${this.schema.stepID}`);
    window.eventBus.$off('files-removed');
    window.eventBus.$off('ai-files-update');
  },
  methods: {
    ...mapActions({
      cancelFileUpload: 'automation/cancelFileUpload',
      updateFileChangeCounter: 'automation/updateFileChangeCounter',
      updateFileUploadCounter: 'automation/updateFileUploadCounter',
      updateFileDeleteCounter: 'automation/updateFileDeleteCounter',
    }),
    cancelUpload() {
      this.$numberInfoModal.show({
        size: ModalSize.MEDIUM,
        title: 'Cancel file processing',
        message: 'File processing is still in progress. Are you sure you want to cancel? Any remaining files will not be processed or deleted.',
        confirmButtonText: 'Yes, cancel',
        cancelButtonText: 'No, don\'t cancel',
        variant: ModalVariant.DANGER,
        confirmCallback: () => {
          this.cancelFileUpload();
          api('POST', `organisation/${this.currentOrganisation.id}/assistant/${this.assistantId}/file/cancel-upload`, null, null, 'w')
            .then((response) => {
              if (response.status === 200) {}
            });
        },
      });
    },
    modalClosed() {
      // this.dirty = true;
      // this.emitData();
    },
    addFiles() {
      this.filesBackup = this.files.addFiles.map((file, index) => `${index}_${file.name}`);
      const filesToRemove = this.files && this.files.removeFiles ? this.files.removeFiles : [];
      const filesToAdd = this.files && this.files.addFiles ? this.files.addFiles : [];
      this.$refs.fileInputModalRef.show([...filesToAdd], [...filesToRemove]);
    },
    calculateFileUploadStats() {
      const fileUploadStats = {
        total: 0,
        uploaded: 0,
        failed: 0,
      };
      this.backgroundActions.filter(ba => ba.assistantId === this.assistantId).forEach((action) => {
        if (action.action === 'uploadAiFiles') {
          fileUploadStats.total += action.data ? action.data.value : 0;
          fileUploadStats.uploaded += action.results ? action.results.success.length : 0;
          fileUploadStats.failed += action.results ? action.results.failed.length : 0;
        }
      });
      this.fileUploadStats = fileUploadStats;
    },
    emitData(reset = false) {
      if (!this.$refs.fileObserver) return;
      this.$refs.fileObserver.validate({ silent: true })
        .then(() => {
          const hasData = !!this.files.addFiles.length || !!this.files.removeFiles.length || !!(this.schema && this.schema.count);
          this.$emit('input', {
            valid: true, // !!this.files.addFiles.length || !!this.files.removeFiles.length || !!(this.schema && this.schema.count),
            name: this.schema.returnKey || '',
            data: {
              value: this.files.addFiles.length,
              files: { ...this.files },
            },
            postAction: 'uploadAiFiles',
            dirty: this.dirty,
            validOneOf: this.schema.validOneOf,
            validOneOfMessage: this.schema.validOneOfMessage,
            hasData, // Indicates that the field has data regardles of it being valid or not. This is used for the form validation where at least one field is required even thou they are not required individually.
          });
          const equal = this.$lodash.isEqual(this.filesBackup, this.files.addFiles) && !this.files.removeFiles;
          if (!equal && hasData && this.schema.editMessage && this.schema.editMessage.default) {
            // Edit message will be triggered only if there are changes on the data and if there are data. Both should be checked because if save is clicked without changes then the empty parameters are set and it is being compared to an empty object resulting with a false positive data available.

            // NOTE: Do not show file update message for now.
            // this.$eventBus.$emit('editMessage', {
            //   key: 'fileInput',
            //   message: this.schema.editMessage.default,
            // });
          }
          // else if (!equal && !hasData && this.schema.editMessage && this.schema.editMessage.empty) {}
        })
        .finally(() => {
          if (reset) this.$refs.fileObserver.reset();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-start-image {
  top: 1px;
  position: relative;
}

.file-button {
  border-radius: 10px !important;
  width: 100%;
  height: 34px;
  padding-top: 8px !important;
  background-image: url('~@/assets/img/SB-Icons/add-files.svg');
  line-height: normal;

  &.disabled {
    opacity: 1 !important;
  }

  &.icon {
    background-size: 14px 14px;
    background-repeat: no-repeat;
    background-position-x: 12px;
    background-position-y: center;

    span {
      padding-left: 16px;
    }
  }

  .subtle {
    font-weight: 400;
  }

  &:hover {
    .subtle {
      color: $blackPrimary;
    }
  }
}
</style>
